import React, { useState, useEffect } from 'react';
import { Mail, MapPin, Send, Instagram, MessageCircle, Eye, FileText, BedDouble } from 'lucide-react';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { trackMeta } from '../lib/metaPixel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const trackEvent = (eventName, eventParams = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
};

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStarted, setFormStarted] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const handleChange = (e) => {
    if (!formStarted) {
      setFormStarted(true);
      trackEvent('form_start', {
        form_name: 'contact_form',
        form_location: 'contact_page'
      });
    }
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleEmailClick = () => {
    trackEvent('email_click', {
      email_address: 'info@yoursetl.co.uk',
      click_location: 'contact_page'
    });
    trackMeta('Contact', { content_name: 'Email Click - Contact Page' });
  };

  const handleInstagramClick = () => {
    trackEvent('instagram_click', {
      instagram_handle: '@yoursetl',
      click_location: 'contact_page'
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        trackEvent('lead_form_complete', {
          form_name: 'contact_form',
          form_location: 'contact_page',
          user_email: formData.email
        });
        trackMeta('Lead', { content_name: 'Contact Form' });
        toast.success('Thank You for Contacting Us!', {
          description: "We've received your message and will reply as soon as possible.",
          duration: 6000,
        });
        setFormData({ name: '', email: '', phone: '', message: '' });
        setFormStarted(false);
      } else {
        throw new Error(data.detail || 'Failed to send message');
      }
    } catch (error) {
      toast.error('Error', {
        description: error.message || 'Something went wrong. Please try again.',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const processSteps = [
    {
      icon: MessageCircle,
      title: 'Initial Conversation',
      description: 'An informal discussion to understand your interest.'
    },
    {
      icon: Eye,
      title: 'Visit the Farm',
      description: 'Experience the setting and the cabins first-hand.'
    },
    {
      icon: BedDouble,
      title: 'Having a Stay',
      description: 'Spend time in a cabin to feel what living here is really like.'
    },
    {
      icon: FileText,
      title: 'Your Proposal',
      description: 'Clear next steps, available plots, and full cost overview.'
    }
  ];

  return (
    <div className="bg-[#f5f1e8]">
      <SEO
        title="Contact Us - Get in Touch | Your Setl"
        description="Interested in buying an exclusive Your Setl Country Cabin in Dorset? Contact us to learn more about ownership, pricing, and our unique concept."
        keywords="contact Your Setl, Dorset cabin ownership, country cabin inquiry, nature retreat contact, Dorset accommodation booking, cabin ownership UK"
        ogImage="https://res.cloudinary.com/dvypx0zh5/image/upload/f_auto,q_auto:best/v1782115840/Schermafbeelding_2026-06-22_100948_rqb9wv.png"
        url="https://www.yoursetl.co.uk/contact"
      />

      {/* Hero Section */}
      <section className="relative h-[60vh] md:h-[60vh] flex items-center justify-center overflow-hidden pt-16 md:pt-0">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://res.cloudinary.com/dvypx0zh5/image/upload/f_auto,q_auto:best/v1771402352/DSC_8097_cedccb.jpg)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#f5f1e8]" />
        </div>

        <div className="relative z-10 text-center px-6 max-w-4xl">
          <h1
            className="text-xl md:text-2xl lg:text-4xl font-light tracking-wide text-[#f5f1e8] mb-4 leading-relaxed"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Interested in buying this exceptional
            <br />
            <span className="text-[#d4a574]">Your Setl</span>
            <br className="md:hidden" />
            <span className="md:ml-2">Country Cabin?</span>
          </h1>
          <p
            className="text-base md:text-xl font-light text-[#f5f1e8]/90"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Get in touch with us
          </p>
        </div>
      </section>

      {/* The Process - Horizontal Steps */}
      <section className="px-6 pb-16 bg-[#f5f1e8]">
        <div className="max-w-4xl mx-auto">
          <h2
            className="text-2xl md:text-3xl font-light text-[#3a2f28] mb-2 text-center"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            The Process
          </h2>
          <p
            className="text-sm md:text-base text-[#3a2f28]/60 mb-10 text-center"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Four steps to your place in nature
          </p>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 md:gap-4 mb-10">
            {processSteps.map((step, i) => (
              <div key={i} className="flex flex-col items-center text-center relative">
                {/* Connector line (desktop only, between steps) */}
                {i < processSteps.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-px bg-[#d4a574]/40" />
                )}
                <div className="w-16 h-16 rounded-full bg-[#3a2f28] flex items-center justify-center mb-4 relative z-10">
                  <step.icon size={24} className="text-[#d4a574]" />
                </div>
                <p
                  className="text-xs uppercase tracking-widest text-[#d4a574] mb-2"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  Step {i + 1}
                </p>
                <h3
                  className="text-base md:text-lg font-light text-[#3a2f28] mb-2"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  {step.title}
                </h3>
                <p
                  className="text-sm text-[#3a2f28]/70 max-w-[220px]"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {step.description}
                </p>
              </div>
            ))}
          </div>

          <p
            className="text-center text-sm text-[#3a2f28]/60 italic"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            This is not a rushed decision. It's about finding the right people for the place.
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-16 px-6 bg-[#f5f1e8]">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div className="bg-[#3a2f28] p-8 md:p-10 rounded-lg">
              <h2
                className="text-2xl md:text-3xl font-light tracking-wide text-[#f5f1e8] mb-8"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Contact Details
              </h2>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-[#d4a574] p-3 rounded-lg">
                    <Mail size={24} className="text-[#3a2f28]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-light text-[#f5f1e8] mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>Email</h3>
                    <a
                      href="mailto:info@yoursetl.co.uk"
                      onClick={handleEmailClick}
                      data-testid="email-link"
                      className="text-base text-[#f5f1e8]/80 hover:text-[#d4a574] transition-colors"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      info@yoursetl.co.uk
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-[#d4a574] p-3 rounded-lg">
                    <Instagram size={24} className="text-[#3a2f28]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-light text-[#f5f1e8] mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>Instagram</h3>
                    <a
                      href="https://www.instagram.com/yoursetl"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={handleInstagramClick}
                      data-testid="instagram-link"
                      className="text-base text-[#f5f1e8]/80 hover:text-[#d4a574] transition-colors"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      @yoursetl
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-[#d4a574] p-3 rounded-lg">
                    <MapPin size={24} className="text-[#3a2f28]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-light text-[#f5f1e8] mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>Location</h3>
                    <a
                      href="https://www.google.com/maps/search/Jurassic+Coast+Dorset+United+Kingdom"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-base text-[#f5f1e8]/80 hover:text-[#d4a574] transition-colors"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      Dorset, United Kingdom
                    </a>
                  </div>
                </div>
              </div>

              {/* Embedded Google Maps */}
              <div className="mt-6 rounded-lg overflow-hidden">
                <iframe
                  title="Your Setl location"
                  src="https://maps.google.com/maps?q=50.65727270734,-2.5271115865080964&t=k&z=15&ie=UTF8&iwloc=&output=embed"
                  width="100%"
                  height="200"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white p-8 md:p-10 rounded-lg border border-[#d4a574]/20">
              <h2
                className="text-2xl md:text-3xl font-light tracking-wide text-[#3a2f28] mb-6"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Send Us a Message
              </h2>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-light text-[#3a2f28] mb-2 tracking-wide" style={{ fontFamily: 'Inter, sans-serif' }}>Name *</label>
                  <input type="text" id="name" name="name" data-testid="contact-name-input" value={formData.name} onChange={handleChange} required className="w-full px-4 py-3 bg-[#f5f1e8]/50 border border-[#d4a574]/30 rounded focus:outline-none focus:border-[#d4a574] focus:ring-1 focus:ring-[#d4a574] transition-colors" style={{ fontFamily: 'Inter, sans-serif' }} />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-light text-[#3a2f28] mb-2 tracking-wide" style={{ fontFamily: 'Inter, sans-serif' }}>Email *</label>
                  <input type="email" id="email" name="email" data-testid="contact-email-input" value={formData.email} onChange={handleChange} required className="w-full px-4 py-3 bg-[#f5f1e8]/50 border border-[#d4a574]/30 rounded focus:outline-none focus:border-[#d4a574] focus:ring-1 focus:ring-[#d4a574] transition-colors" style={{ fontFamily: 'Inter, sans-serif' }} />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-light text-[#3a2f28] mb-2 tracking-wide" style={{ fontFamily: 'Inter, sans-serif' }}>Phone (Optional)</label>
                  <input type="tel" id="phone" name="phone" data-testid="contact-phone-input" value={formData.phone} onChange={handleChange} className="w-full px-4 py-3 bg-[#f5f1e8]/50 border border-[#d4a574]/30 rounded focus:outline-none focus:border-[#d4a574] focus:ring-1 focus:ring-[#d4a574] transition-colors" style={{ fontFamily: 'Inter, sans-serif' }} />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-light text-[#3a2f28] mb-2 tracking-wide" style={{ fontFamily: 'Inter, sans-serif' }}>Message *</label>
                  <textarea id="message" name="message" data-testid="contact-message-input" value={formData.message} onChange={handleChange} required rows={5} className="w-full px-4 py-3 bg-[#f5f1e8]/50 border border-[#d4a574]/30 rounded focus:outline-none focus:border-[#d4a574] focus:ring-1 focus:ring-[#d4a574] transition-colors resize-none" style={{ fontFamily: 'Inter, sans-serif' }} />
                </div>
                <button type="submit" disabled={isSubmitting} data-testid="contact-submit-btn" className="w-full px-8 py-4 bg-[#3a2f28] text-[#f5f1e8] font-light tracking-wide hover:bg-[#d4a574] transition-all duration-300 rounded flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed" style={{ fontFamily: 'Inter, sans-serif' }}>
                  <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
                  {!isSubmitting && <Send size={18} />}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Your Setl Concept Image */}
      <section className="px-6 py-12 bg-[#f5f1e8]">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-lg overflow-hidden shadow-lg">
            <img
              src="https://res.cloudinary.com/dvypx0zh5/image/upload/f_auto,q_auto:best/v1782115840/Schermafbeelding_2026-06-22_100948_rqb9wv.png"
              alt="Your Setl concept overview"
              className="w-full h-auto"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Closing */}
      <section className="py-16 px-6 bg-[#3a2f28]">
        <div className="max-w-4xl mx-auto text-center">
          <h2
            className="text-2xl md:text-3xl font-light tracking-wide text-[#f5f1e8] mb-6"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Ready to Begin Your Journey?
          </h2>
          <p
            className="text-lg leading-relaxed text-[#f5f1e8]/90"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            We look forward to welcoming you to Your Setl, where nature, comfort, and authentic connection come together to create something truly special.
          </p>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
